// Deep import rather than the `@/common/di` barrel, which re-exports `./datasource` — and that
// module imports `@/features`, dragging the whole feature graph and its initialisation cycle in
// behind a request for one axios instance.
import { AxiosSingleton } from '@/common/di/axios';
// `import type`, so the statement is erased at build time rather than pulling the feature barrel in
// at runtime. Every name here is a type or is used only in type position. The barrel has an
// initialisation cycle — it exports `./data` before `./domain`, so a mapper reaching back through
// it for an enum sees `undefined` — and importing it from here made this module impossible to load
// in isolation. Nothing about the emitted app changes; the types are identical.
import type {
  GetCentresModel,
  GetClosuresModel,
  GetEventsModel,
  GetFireDangerRatingsModelV2,
  GetIncidentsModel,
  GetWarningByIdModel,
  GetWarningDetailModel,
  GetWarningIndexModel,
  GetWarningsModel,
  HazardsDataSource,
} from '@/features/hazards';
import { Axios } from 'axios';
import { container } from 'tsyringe';
import type { GetTotalFireBansModel } from '../models/hazards/get-fireban-model';

/** The only prefix a detail path may have, and the segment that must precede its version. */
const DETAIL_PATH_PREFIX = '/v1/warnings/';
const DETAIL_SEGMENT = 'detail';

/**
 * Validate a server-supplied `detail.url` before following it.
 *
 * The index advertises this path and the client follows it verbatim — the URL is opaque, so the
 * client never constructs it from `id` and `version`. Opaque is not the same as unvalidated: a
 * spoofed or tampered index response could otherwise point the browser at an arbitrary host, and
 * this is the one value in this layer that reaches the network unexamined.
 *
 * These checks are ported from the mobile client's `_resolveDetailUri` rather than invented here,
 * because that function has already been through review and the two clients must not diverge on a
 * security boundary.
 *
 * Throws rather than returning null, so the repository handles a bad URL exactly as it handles a
 * failed fetch (KUDO1-9541).
 */
export const assertSafeDetailUrl = (url: string): void => {
  const reject = (reason: string): never => {
    // The rejected value is deliberately not interpolated into the message: it is attacker-supplied
    // and this string reaches logs and Sentry.
    throw new Error(`Refusing to follow an unsafe detail url: ${reason}`);
  };

  if (!url) reject('empty');

  // Checked BEFORE parsing, and it has to be. A decoded `%2F` reappears as a path separator, so a
  // post-parse segment check cannot see it — this is the one traversal form that survives parsing.
  if (url.includes('%')) reject('percent-encoding');

  // A scheme, an authority, or a fragment. `//host/path` is protocol-relative and would send the
  // request off-origin while still looking like a path.
  if (url.includes('#')) reject('fragment');
  if (url.startsWith('//')) reject('authority');
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) reject('scheme');

  if (!url.startsWith('/')) reject('not an absolute path');

  // Axios resolves with WHATWG rules, which treat `\` as a path separator. The segment scan below
  // splits on `/` only, so `/v1/warnings/x/detail/..\..\..\..\admin` clears every other check here
  // and still resolves to `/admin`. Tab, LF and CR are stripped during that same resolution, which
  // lets them hide a separator from any check that runs on the raw string.
  if (/[\\\t\n\r]/.test(url)) reject('separator or control character');

  const [path] = url.split('?');
  const segments = path.split('/').slice(1);

  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
    reject('empty or dot segment');
  }

  if (!path.startsWith(DETAIL_PATH_PREFIX)) reject('outside the warnings detail namespace');
  if (segments.at(-2) !== DETAIL_SEGMENT) reject('not a versioned detail resource');
};

/**
 * Every path here carries its own `/v1` prefix.
 *
 * The prefix used to live on `portalApi` in the ten config files, which made it impossible to
 * follow a server-supplied absolute path: `baseURL + '/v1/warnings/…'` would have produced
 * `…/v1/v1/warnings/…`. The index advertises `detail.url` as an absolute path from the host root,
 * so the prefix had to move to the call sites (KUDO1-9541).
 *
 * Only the `.ewa` instance is versioned. `.s3` and `.ewaPublicBff` are unchanged.
 */
export class RemoteHazardsDataSource implements HazardsDataSource {
  api: Axios;

  constructor() {
    this.api = container.resolve(AxiosSingleton).ewa;
  }

  async getOutagesAndClosures(): Promise<(GetClosuresModel | GetWarningsModel)[]> {
    const res = await this.api.get('/v1/outages-closures');
    return res.data.outagesClosures;
  }

  async getClosures(): Promise<GetClosuresModel[]> {
    const res = await this.api.get('/v1/closures');
    return res.data.closures;
  }

  async getCentres(): Promise<GetCentresModel[]> {
    const res = await this.api.get('/v1/centres');
    return res.data.centres;
  }

  async getWarnings(): Promise<GetWarningsModel[]> {
    const res = await this.api.get('/v1/warnings');
    return res.data.warnings;
  }

  /**
   * The index: every warning, with `geo-source` filtered to Point features.
   *
   * The envelope is deliberately identical to `/v1/warnings`, so this is `getWarnings()` with one
   * path changed and no model change on the client (KUDO1-9541).
   */
  async getWarningsIndex(): Promise<GetWarningIndexModel[]> {
    const res = await this.api.get('/v1/warnings/index');
    return res.data.warnings;
  }

  /**
   * One warning's full record, including the boundary geometry the index omits.
   *
   * `url` comes from the index's `detail.url` and is followed verbatim — never constructed here.
   * It is validated first: see `assertSafeDetailUrl`.
   *
   * `signal` lets the caller's retry loop own cancellation, so an abandoned attempt releases its
   * socket instead of holding one until the instance timeout fires.
   */
  async getWarningDetail(url: string, signal?: AbortSignal): Promise<GetWarningDetailModel> {
    assertSafeDetailUrl(url);

    const res = await this.api.get(url, { signal });
    return res.data.warning;
  }

  async getIncidents(): Promise<GetIncidentsModel[]> {
    const res = await this.api.get('/v1/incidents');
    return res.data.incidents;
  }

  async getEvents(): Promise<GetEventsModel[]> {
    const res = await this.api.get('/v1/events');
    return res.data.events;
  }

  async getTotalFireBans(): Promise<GetTotalFireBansModel[]> {
    const res = await this.api.get('/v1/total-fire-bans');
    return res.data.totalFireBans;
  }

  /**
   * @deprecated Broken before this change and deliberately still broken after it.
   *
   * `api.ts` routes no GET on `/v1/warnings/{warningId}`, so this 403s. The prefix was added for
   * consistency only. It must NOT be repaired into the new `{warningId}/detail/{version}` chain:
   * that resource is addressed per revision and carries no unversioned method, which is what makes
   * every detail URL version-bearing by construction.
   */
  async getWarningById(warningId: string): Promise<GetWarningByIdModel> {
    const res = await this.api.get(`/v1/warnings/${warningId}`);
    return res.data;
  }

  async getFireDangerRatings(): Promise<GetFireDangerRatingsModelV2[]> {
    const res = await this.api.get('/v1/fire-danger-ratings');
    return res.data.fireDangerRatings as GetFireDangerRatingsModelV2[];
  }
}
